import { useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { formatarDinheiro } from '@/pages/gerenciar/utils';
import { corSemaforo } from '../utils';
import ImagemProduto from '@/components/feature/ImagemProduto';
import type { ProdutoPainel, PontoPainel } from '@/pages/fichas/types';

interface Props {
  produtos: ProdutoPainel[];
  pontos: PontoPainel[];
  custos: Map<number, number | null>;
  semCustoInicial?: boolean;
  rotuloPonto?: string;
  aoCardapio: (pontoId: number) => void;
  aoNovoProduto: (pontoId: number) => void;
  aoNovaBarraca?: () => void;
  aoEditar: (p: ProdutoPainel) => void;
  aoSalvo: () => void;
}

function textoEstoque(p: ProdutoPainel): string {
  if (p.estoque_total == null) return 'sem controle de estoque';
  const acabando = p.semaforo === 'amarelo' ? ' · acabando' : '';
  const esgotou = p.esgotado ? ' · esgotou' : '';
  return `${p.vendido} de ${p.estoque_total}${acabando}${esgotou}`;
}

function temCusto(p: ProdutoPainel, custos: Map<number, number | null>): boolean {
  return (custos.get(p.produto_id) ?? 0) > 0;
}

// Produtos agrupados por ponto, com a mesma cara da vitrine: imagem,
// nome inteiro, preço em destaque, barra de estoque e interruptor à venda.
// Os grupos saem de `pontos` (todas as barracas), então uma barraca sem
// produto também aparece — com o botão "+ Produto".
export default function PainelProdutos({
  produtos,
  pontos,
  custos,
  semCustoInicial = false,
  rotuloPonto = 'Barraca',
  aoCardapio,
  aoNovoProduto,
  aoNovaBarraca,
  aoEditar,
  aoSalvo,
}: Props) {
  const [trocandoId, setTrocandoId] = useState<number | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [soSemCusto, setSoSemCusto] = useState(semCustoInicial);

  const visiveis = useMemo(
    () => (soSemCusto ? produtos.filter((p) => !temCusto(p, custos)) : produtos),
    [produtos, custos, soSemCusto],
  );

  const grupos = useMemo(() => {
    const ordem = pontos.map((p) => p.ponto_id);
    const mapa = new Map<number, ProdutoPainel[]>();
    visiveis.forEach((p) => {
      const lista = mapa.get(p.ponto_id) ?? [];
      lista.push(p);
      mapa.set(p.ponto_id, lista);
    });

    // Todas as barracas (mesmo as que ainda não têm produto). Com o filtro
    // "Sem custo" ligado, pula as que não têm produto visível.
    const base = pontos
      .map((p) => ({
        pontoId: p.ponto_id,
        nome: p.nome,
        produtos: mapa.get(p.ponto_id) ?? [],
      }))
      .filter((g) => !soSemCusto || g.produtos.length > 0);

    // Produtos de uma barraca que não está na lista (extras) continuam no fim.
    const extras = [...mapa.keys()]
      .filter((id) => !ordem.includes(id))
      .map((id) => ({
        pontoId: id,
        nome: mapa.get(id)![0].ponto,
        produtos: mapa.get(id)!,
      }));

    return [...base, ...extras];
  }, [visiveis, pontos, soSemCusto]);

  async function alternarEsgotado(p: ProdutoPainel) {
    setTrocandoId(p.produto_id);
    setErro(null);
    try {
      const { error } = await supabase.rpc('loja_marcar_esgotado', {
        p_produto: p.produto_id,
        p_esgotado: !p.esgotado,
      });
      if (error) throw error;
      aoSalvo();
    } catch (e) {
      setErro(mensagemAmigavel(e));
    } finally {
      setTrocandoId(null);
    }
  }

  // Sem barraca nenhuma e sem produto: pede para criar a barraca primeiro.
  if (pontos.length === 0 && produtos.length === 0) {
    return (
      <div className="rounded-3xl bg-white p-5 shadow-[0_6px_18px_rgba(20,25,60,.06)]">
        <h2 className="text-[17px] font-bold text-foreground-950">Produtos</h2>
        <p className="mt-3 text-[14px] text-foreground-500">
          Crie uma {rotuloPonto.toLowerCase()} primeiro: é lá que os produtos ficam.
        </p>
        {aoNovaBarraca && (
          <button
            type="button"
            onClick={aoNovaBarraca}
            className="mt-3 flex min-h-[48px] w-full cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-2xl border border-dashed border-primary-300 px-4 text-[14px] font-semibold text-primary-700 transition-colors hover:bg-primary-50"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-add-line" />
            </span>
            Criar {rotuloPonto.toLowerCase()}
          </button>
        )}
      </div>
    );
  }

  return (
    <div>
      {erro && <p className="mb-3 rounded-2xl bg-danger-50 px-4 py-3 text-[14px] text-danger-700">{erro}</p>}

      {/* Filtro por custo — só faz sentido quando já existe produto */}
      {produtos.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setSoSemCusto(false)}
            className={`flex h-9 cursor-pointer items-center whitespace-nowrap rounded-full border px-4 text-[13px] font-semibold transition-colors ${
              !soSemCusto
                ? 'border-primary-500 bg-primary-500 text-white'
                : 'border-primary-100 bg-white text-foreground-700 hover:bg-primary-50'
            }`}
          >
            Todos
          </button>
          <button
            type="button"
            onClick={() => setSoSemCusto(true)}
            className={`flex h-9 cursor-pointer items-center whitespace-nowrap rounded-full border px-4 text-[13px] font-semibold transition-colors ${
              soSemCusto
                ? 'border-primary-500 bg-primary-500 text-white'
                : 'border-primary-100 bg-white text-foreground-700 hover:bg-primary-50'
            }`}
          >
            Sem custo
          </button>
        </div>
      )}

      {grupos.length === 0 ? (
        <div className="rounded-3xl bg-white p-6 text-center shadow-[0_6px_18px_rgba(20,25,60,.06)]">
          <p className="text-[14px] text-foreground-500">
            Todos os produtos já têm custo informado.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {grupos.map((g) => (
            <div
              key={g.pontoId}
              className="rounded-3xl bg-white p-4 shadow-[0_6px_18px_rgba(20,25,60,.06)]"
            >
              <div className="flex items-center justify-between gap-2 px-1">
                <div className="flex min-w-0 items-center gap-2">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                    <i className="ri-store-2-line text-base" />
                  </span>
                  <h3 className="truncate text-[15px] font-bold text-foreground-900">{g.nome}</h3>
                </div>
                <button
                  type="button"
                  onClick={() => aoCardapio(g.pontoId)}
                  className="flex shrink-0 cursor-pointer items-center gap-1 whitespace-nowrap rounded-full border border-primary-100 bg-white px-3 py-1.5 text-[13px] font-semibold text-primary-700 transition-colors hover:bg-primary-50"
                >
                  Cardápio
                </button>
              </div>

              {g.produtos.length === 0 ? (
                <p className="mt-2 px-1 text-[14px] text-foreground-500">
                  Nenhum produto nesta barraca ainda.
                </p>
              ) : (
                <ul className="mt-2">
                  {g.produtos.map((p) => {
                    const trocando = trocandoId === p.produto_id;
                    const custoProduto = custos.get(p.produto_id) ?? 0;
                    const percentual =
                      p.percentual_vendido == null
                        ? 0
                        : Math.max(0, Math.min(100, p.percentual_vendido));
                    const aVenda = !p.esgotado && p.situacao !== 'fechado';
                    return (
                      <li key={p.produto_id} className="flex items-center gap-3 py-3">
                        <button
                          type="button"
                          onClick={() => aoEditar(p)}
                          className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 text-left"
                        >
                          <ImagemProduto
                            imagemUrl={p.imagem_url}
                            imagemSlug={p.imagem_slug}
                            emoji={p.emoji}
                            nome={p.nome}
                            tamanho="media"
                          />
                          <span className="min-w-0 flex-1">
                            <span className="line-clamp-2 text-[15px] font-semibold leading-snug text-foreground-950">
                              {p.nome}
                            </span>
                            <span className="mt-0.5 block text-[15px] font-bold text-primary-700">
                              {formatarDinheiro(p.preco_centavos)}
                            </span>
                            {custoProduto > 0 && (
                              <span className="mt-0.5 block text-[12px] text-foreground-500">
                                lucro {formatarDinheiro(p.preco_centavos - custoProduto)}/un
                              </span>
                            )}

                            {p.estoque_total != null ? (
                              <span className="mt-1.5 block h-1.5 w-full max-w-[200px] overflow-hidden rounded-full bg-background-100">
                                <span
                                  className={`block h-full rounded-full ${corSemaforo(p.semaforo)}`}
                                  style={{ width: `${percentual}%` }}
                                />
                              </span>
                            ) : null}

                            <span className="mt-1 block text-[12px] text-foreground-500">
                              {textoEstoque(p)}
                            </span>

                            {p.venda_antecipada && (
                              <span className="mt-1 inline-block rounded-full bg-accent-100 px-2 py-0.5 text-[12px] font-semibold text-accent-900">
                                venda antecipada
                              </span>
                            )}
                            {p.situacao === 'fechado' && (
                              <span className="mt-1 inline-block rounded-full bg-secondary-100 px-2 py-0.5 text-[12px] font-semibold text-foreground-600">
                                ninguém vê ainda (vendas fechadas)
                              </span>
                            )}
                          </span>
                        </button>

                        <button
                          type="button"
                          role="switch"
                          aria-checked={aVenda}
                          aria-label={aVenda ? `Pausar ${p.nome}` : `Colocar ${p.nome} à venda`}
                          onClick={() => alternarEsgotado(p)}
                          disabled={trocando}
                          className={`relative h-7 w-12 shrink-0 cursor-pointer rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                            aVenda ? 'bg-success-500' : 'bg-secondary-300'
                          }`}
                        >
                          <span
                            className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow-sm transition-all ${
                              aVenda ? 'left-6' : 'left-1'
                            }`}
                          />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}

              <button
                type="button"
                onClick={() => aoNovoProduto(g.pontoId)}
                className="mt-1 flex min-h-[48px] w-full cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-2xl border border-dashed border-primary-300 px-4 text-[14px] font-semibold text-primary-700 transition-colors hover:bg-primary-50"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-add-line" />
                </span>
                Produto
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}